import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { usePageMeta } from "../hooks/usePageMeta";
import {
  getCookieConsent,
  setCookieConsent,
  COOKIE_CONSENT_ACCEPTED,
  COOKIE_CONSENT_ESSENTIAL,
} from "../lib/cookieConsent";

export default function CookiesPage() {
  const [choice, setChoice] = useState(null);

  usePageMeta({
    path: "/cookies",
    title: "Cookie policy , MediTalk",
    description:
      "What cookies MediTalk sets, what they do, and how to change your consent at any time.",
  });

  useEffect(() => {
    setChoice(getCookieConsent());
  }, []);

  const update = (value) => {
    setCookieConsent(value);
    setChoice(value);
    toast.success(
      value === COOKIE_CONSENT_ACCEPTED
        ? "Analytics cookies enabled."
        : "Analytics cookies disabled.",
    );
  };

  return (
    <div className="bg-white">
      <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/10 blur-3xl pointer-events-none"
        />
        <div className="container-main py-12 md:py-16 relative z-10 max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-3">
            <Cookie size={14} />
            Legal
          </span>
          <h1 className="font-['Outfit'] font-bold text-white tracking-tight text-3xl md:text-5xl mb-4 leading-[1.05]">
            Cookie policy
          </h1>
          <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-2xl">
            What we set, what it does, and how to change your mind at any time.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container-main max-w-3xl">
          <div className="text-[15px] md:text-[15.5px] text-[var(--text-muted)] leading-relaxed space-y-7">
            <p>
              MediTalk is operated by Medicys<span className="text-[0.55em] align-top tracking-normal ml-px" aria-label="registered trademark">®</span> Limited (company number{" "}
              <strong>04504403</strong>). This cookie policy explains the
              cookies and similar technologies we set on meditalk.world and
              how you can control them. For broader information on how we
              handle personal data, see our{" "}
              <Link to="/privacy" className="ml-policy-link">
                privacy notice
              </Link>
              .
            </p>

            <Section title="1. What's a cookie?">
              <p>
                A cookie is a small piece of data a website stores in your
                browser. Some cookies are essential (the site won't work
                properly without them), others are optional and help us
                understand how the site is used.
              </p>
            </Section>

            <Section title="2. Cookies we set">
              <div className="not-prose">
                <div className="overflow-x-auto -mx-2 md:mx-0">
                  <table className="min-w-full text-[13.5px] text-[var(--charcoal)] border border-[var(--light-border)] rounded-xl overflow-hidden">
                    <thead>
                      <tr className="bg-[var(--bone-soft)] text-left">
                        <th className="px-4 py-3 font-semibold">Name</th>
                        <th className="px-4 py-3 font-semibold">Type</th>
                        <th className="px-4 py-3 font-semibold">Set by</th>
                        <th className="px-4 py-3 font-semibold">Purpose</th>
                        <th className="px-4 py-3 font-semibold">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--light-border)]">
                      <tr>
                        <td className="px-4 py-3 font-mono text-[12.5px]">meditalk:cookie-consent</td>
                        <td className="px-4 py-3">Essential</td>
                        <td className="px-4 py-3">MediTalk</td>
                        <td className="px-4 py-3">Remembers your cookie choice so we don't ask again.</td>
                        <td className="px-4 py-3">Persistent (localStorage)</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 font-mono text-[12.5px]">meditalk:patient-register / caregiver-register</td>
                        <td className="px-4 py-3">Essential</td>
                        <td className="px-4 py-3">MediTalk</td>
                        <td className="px-4 py-3">Save-and-resume for the sign-up form (your draft stays on your device).</td>
                        <td className="px-4 py-3">Persistent (localStorage)</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 font-mono text-[12.5px]">ph_*</td>
                        <td className="px-4 py-3">Analytics (optional)</td>
                        <td className="px-4 py-3">PostHog</td>
                        <td className="px-4 py-3">Anonymous product analytics + occasional session replay to help us improve the site. Only loaded if you've accepted.</td>
                        <td className="px-4 py-3">Up to 12 months</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </Section>

            <Section title="3. Your choice">
              <p>
                Optional analytics cookies are off by default. You can change
                your choice any time using the buttons below , the change
                takes effect immediately.
              </p>
              <div className="mt-4 rounded-2xl border border-[var(--light-border)] bg-[var(--bone-soft)] p-5">
                <p className="text-[13.5px] text-[var(--charcoal)] mb-4">
                  Current setting:{" "}
                  <strong>
                    {choice === COOKIE_CONSENT_ACCEPTED
                      ? "All cookies accepted (essential + analytics)"
                      : choice === COOKIE_CONSENT_ESSENTIAL
                        ? "Essential cookies only"
                        : "Not set yet"}
                  </strong>
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={() => update(COOKIE_CONSENT_ACCEPTED)}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-semibold text-[13px] hover:bg-[var(--lime-dark)] transition-colors"
                    data-testid="cookies-page-accept"
                  >
                    <Check size={14} strokeWidth={3} />
                    Accept all
                  </button>
                  <button
                    type="button"
                    onClick={() => update(COOKIE_CONSENT_ESSENTIAL)}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-white text-[var(--charcoal)] font-semibold text-[13px] border border-[var(--light-border)] hover:bg-[var(--bone-soft)] transition-colors"
                    data-testid="cookies-page-reject"
                  >
                    <XIcon size={14} strokeWidth={3} />
                    Essential only
                  </button>
                </div>
              </div>
            </Section>

            <Section title="4. Browser controls">
              <p>
                Your browser also lets you clear or block cookies. The{" "}
                <a
                  href="https://www.aboutcookies.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-policy-link"
                >
                  aboutcookies.org
                </a>{" "}
                site has step-by-step instructions for every major browser.
                Note that blocking essential cookies may stop parts of the site
                working.
              </p>
            </Section>

            <Section title="5. Questions">
              <p>
                Anything else? Email{" "}
                <a className="ml-policy-link" href="mailto:privacy@medicysltd.co.uk">
                  privacy@medicysltd.co.uk
                </a>
                .
              </p>
            </Section>
          </div>
        </div>
      </section>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-xl md:text-2xl mb-3">
      {title}
    </h2>
    <div className="space-y-3">{children}</div>
  </section>
);
